import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import { type EnhancedToolResult, type ToolExecutionContext } from '../types';
import { Tool } from './Tool';

import { getCanUseAIAvatar } from 'js/src/data/account/account-selectors';
import {
    ensureAIAvatarsLoaded,
    getAIAvatarVoicePresetsOrm,
} from 'js/src/data/ai-voices-and-avatars';

export const listAvatarsSchema = z.object({});
export type ListAvatarsArgs = z.infer<typeof listAvatarsSchema>;

export class ListAvatarsTool extends Tool<typeof listAvatarsSchema> {
    protected override readonly schema = listAvatarsSchema;
    protected override readonly toolName = TOOL_NAMES.listAvatars;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription = `List the AI avatars available to this team — the on-screen presenters an avatar scene group can use. Each result includes the avatar's id, name, and the voice paired with it. Pass the chosen id as \`avatar_id\` to ${TOOL_NAMES.addAvatarSceneGroup}.`;

    public override getToolDisplayName(): string {
        return 'Listing avatars';
    }

    public get statusIcon(): string {
        return 'face';
    }

    public async execute(
        _args: ListAvatarsArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        if (!getCanUseAIAvatar(this.getState())) {
            return { result: 'AI avatars are not available on this account.', isSuccess: false };
        }

        await this.dispatch(ensureAIAvatarsLoaded());

        // The same voice-preset gallery the script composer offers; the raw avatar list also
        // holds ingested avatars that were never processed for the editor.
        const presetOrm = getAIAvatarVoicePresetsOrm(this.getState());
        const presetIds = presetOrm.allIds;
        if (presetIds.length === 0) {
            return { result: 'No AI avatars are available for this account.', isSuccess: true };
        }

        const lines = presetIds.map((presetId, index) => {
            const preset = presetOrm.getByIdOrThrow(presetId);
            return `[${index + 1}] id=${preset.avatar_id}  "${preset.name}"  voice=${preset.voice_slug}`;
        });

        return {
            result:
                `Found ${lines.length} avatar${lines.length === 1 ? '' : 's'}:\n` +
                lines.join('\n') +
                `\n\nPass the chosen id as \`avatar_id\` to ${TOOL_NAMES.addAvatarSceneGroup}.`,
            isSuccess: true,
        };
    }
}
